/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, type Ref } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { css } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';

import type { CONVERSATION_ASSISTANT_MINIMUM_HEIGHT as CONVERSATION_ASSISTANT_MINIMUM_HEIGHT_TYPE } from '../../constants/public';
import { useUIConfig } from '../../utils/ui-config/useUIConfig';

const CONVERSATION_ASSISTANT_MINIMUM_HEIGHT =
	350 satisfies typeof CONVERSATION_ASSISTANT_MINIMUM_HEIGHT_TYPE;

const styles = cssMap({
	wrapper: {
		boxSizing: 'border-box',
		width: `calc(100% - ${token('space.150')})`,
		marginTop: token('space.200'),
		height: `calc(100% - ${token('space.200')})`,
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		alignContent: 'stretch',
		alignItems: 'stretch',
	},

	wrapperNav4: {
		boxSizing: 'border-box',
		width: '100%',
		height: '100%',
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		alignContent: 'stretch',
		alignItems: 'stretch',
	},

	wrapperHeight: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		minHeight: `${CONVERSATION_ASSISTANT_MINIMUM_HEIGHT}px`,
	},

	externalEmbed: {
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		width: '100%',
		height: '100%',
	},

	fullscreenDebug: {
		position: 'fixed',
		inset: token('space.0'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		height: 'unset',
		width: 'unset',
	},
	/* When the staging area is managed externally via stagingAreaRef, chat should fill its container fully */
	withExternalStagingArea: {
		width: '100%',
	},
});

const stylesBorder = cssMap({
	borderNavV3: {
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderTopLeftRadius: token('radius.large'),
		borderTopRightRadius: token('radius.large'),
	},

	/**
	 * The Nav 4 `Panel` page layout slot will have a `1px` left border.
	 *
	 * No border is required by the consumer.
	 */
	borderNavV4: {},

	borderFullScreenDebug: {
		borderStyle: 'solid',
		borderColor: token('color.border.accent.blue'),
		borderWidth: token('radius.large'),
		borderRadius: token('radius.large'),
	},

	// empty on purpose so every type of border variation has styles defining, this is to avoid linter error
	borderExternalEmbed: {},

	borderStagingArea: {
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.raised'),
	},
});

const miniModalWrapperStyles = css({
	borderRadius: token('radius.xxlarge'),
	boxShadow: token('elevation.shadow.overlay'),
	overflow: 'hidden',
	// height: auto lets the mini-modal shell (position: fixed; bottom: 0) expand upward when resized.
	height: 'auto',
	// default values used by @atlaskit/motion's easeInOut and medium duration
	transitionProperty: 'max-height',
	transitionDuration: token('motion.duration.xlong', '350ms'),
	transitionTimingFunction: token('motion.easing.out.practical', 'cubic-bezier(0.15,1,0.3,1)'),
});

type SidePanelSurfaceProps = {
	children: ReactNode;
	testId?: string;
	isNav4Enabled?: boolean;
	isMiniModal?: boolean;
	isFullscreenDebug?: boolean;
	/**
	 * Marks the element as the Rovo Chat panel (`data-rovo-chat-panel`), which other chat
	 * features use to detect whether a DOM node is inside the chat.
	 */
	isChatPanel?: boolean;
	surfaceRef?: Ref<HTMLDivElement>;
};

/**
 * The surface (background, border, elevation and sizing) of the Rovo Chat panel and of any side
 * panel that takes the chat's place, so they all look the same.
 */
export const SidePanelSurface = ({
	children,
	testId,
	isNav4Enabled,
	isMiniModal,
	isFullscreenDebug,
	isChatPanel,
	surfaceRef,
}: SidePanelSurfaceProps): JSX.Element => {
	const { externalEmbed, fullScreen, elements } = useUIConfig();
	const { stagingAreaMode } = useStagingAreaState();

	const shouldShowStagingArea = stagingAreaMode === 'inline';
	const chatPanelAttribute = isChatPanel ? 'true' : undefined;

	if (isFullscreenDebug) {
		return (
			<div
				data-testid={testId}
				data-rovo-chat-panel={chatPanelAttribute}
				ref={surfaceRef}
				css={[
					isNav4Enabled ? styles.wrapperNav4 : styles.wrapper,
					isMiniModal ? miniModalWrapperStyles : styles.wrapperHeight,
					externalEmbed && styles.externalEmbed,
					styles.fullscreenDebug,
					stylesBorder.borderFullScreenDebug,
				]}
			>
				{children}
			</div>
		);
	}

	const getBorderStyle = (): keyof typeof stylesBorder => {
		if (elements?.stagingAreaEnabled) {
			return 'borderStagingArea';
		}

		if (externalEmbed) {
			return 'borderExternalEmbed';
		}

		if (isNav4Enabled) {
			return 'borderNavV4';
		}

		return 'borderNavV3';
	};

	return (
		<div
			data-testid={testId}
			data-rovo-chat-panel={chatPanelAttribute}
			ref={surfaceRef}
			css={[
				isNav4Enabled ? styles.wrapperNav4 : styles.wrapper,
				isMiniModal ? miniModalWrapperStyles : styles.wrapperHeight,
				externalEmbed && styles.externalEmbed,
				stylesBorder[getBorderStyle()],
				fullScreen && shouldShowStagingArea && styles.withExternalStagingArea,
			]}
		>
			{children}
		</div>
	);
};
